/**
 * VentasTab.js - Tab de historial de ventas con filtros de fecha
 */
import React, { useState, useEffect, useCallback } from "react";
import { getAuthHeaders } from "../../../services/api";

function VentasTab() {
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filtros de fecha
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [presetActivo, setPresetActivo] = useState('mes');

  // Cargar ventas
  const loadVentas = useCallback(async (desde = null, hasta = null) => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      let url = '/api/objetos/mkt/ventas?limit=200';

      if (desde) url += `&desde=${desde}`;
      if (hasta) url += `&hasta=${hasta}`;

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error('Error al cargar ventas');
      }

      const data = await response.json();
      setVentas(data.data || []);
    } catch (err) {
      console.error('Error cargando ventas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Aplicar preset de fecha
  const aplicarPreset = (preset) => {
    setPresetActivo(preset);
    const hoy = new Date();
    let desde = new Date();

    switch (preset) {
      case 'hoy':
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
        break;
      case 'semana':
        const diaSemana = hoy.getDay();
        desde = new Date(hoy);
        desde.setDate(hoy.getDate() - diaSemana);
        break;
      case 'mes':
        desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        break;
      case 'anio':
        desde = new Date(hoy.getFullYear(), 0, 1);
        break;
      case 'todo':
        setFechaDesde('');
        setFechaHasta('');
        loadVentas();
        return;
      default:
        break;
    }

    const desdeStr = desde.toISOString().split('T')[0];
    const hastaStr = hoy.toISOString().split('T')[0];

    setFechaDesde(desdeStr);
    setFechaHasta(hastaStr);
    loadVentas(desdeStr, hastaStr);
  };

  // Aplicar filtro manual
  const aplicarFiltro = () => {
    setPresetActivo(null);
    loadVentas(fechaDesde || null, fechaHasta || null);
  };

  // Cargar datos iniciales (este mes)
  useEffect(() => {
    aplicarPreset('mes');
  }, []);

  // Calcular resumen
  const resumen = ventas.reduce((acc, venta) => {
    const tipo = venta.tipo || 'otro';
    const plan = venta.plan || 'light';

    if (!acc.porTipo[tipo]) acc.porTipo[tipo] = { total: 0, planes: {} };
    acc.porTipo[tipo].total++;
    acc.porTipo[tipo].planes[plan] = (acc.porTipo[tipo].planes[plan] || 0) + 1;

    return acc;
  }, { porTipo: {} });

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    const d = new Date(fecha);
    return d.toLocaleString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="ventas-tab">
      {/* Filtros de fecha */}
      <div className="date-filter mb-4">
        <div className="row align-items-end g-3">
          {/* Presets */}
          <div className="col-12 col-md-auto">
            <label className="form-label small text-muted">Período</label>
            <div className="d-flex flex-wrap gap-2">
              {['hoy', 'semana', 'mes', 'anio', 'todo'].map((preset) => (
                <button
                  key={preset}
                  className={`date-preset ${presetActivo === preset ? 'active' : ''}`}
                  onClick={() => aplicarPreset(preset)}
                >
                  {preset === 'hoy' ? 'Hoy' :
                    preset === 'semana' ? 'Esta semana' :
                      preset === 'mes' ? 'Este mes' :
                        preset === 'anio' ? 'Este año' : 'Todo'}
                </button>
              ))}
            </div>
          </div>

          {/* Fechas manuales */}
          <div className="col-12 col-md">
            <div className="row g-2">
              <div className="col">
                <label className="form-label small text-muted">Desde</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPresetActivo(null);
                  }}
                />
              </div>
              <div className="col">
                <label className="form-label small text-muted">Hasta</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPresetActivo(null);
                  }}
                />
              </div>
              <div className="col-auto d-flex align-items-end">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={aplicarFiltro}
                  disabled={loading}
                >
                  <i className="bi bi-search"></i> Buscar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-success" role="status"></div>
          <span className="ms-2 text-muted">Cargando ventas...</span>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Resumen del período */}
      {!loading && ventas.length > 0 && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col">
                <h6 className="mb-1">
                  <i className="bi bi-calendar-check me-2 text-success"></i>
                  Resumen del Período
                </h6>
                <p className="text-muted small mb-0">
                  {fechaDesde && fechaHasta
                    ? `${fechaDesde} al ${fechaHasta}`
                    : 'Todas las ventas'}
                </p>
              </div>
              <div className="col-auto">
                <span className="display-6 text-success fw-bold">{ventas.length}</span>
                <span className="text-muted ms-2">vendidos</span>
              </div>
            </div>

            {/* Desglose por tipo */}
            {Object.keys(resumen.porTipo).length > 0 && (
              <div className="mt-3 pt-3 border-top">
                <div className="row g-3">
                  {Object.entries(resumen.porTipo).map(([tipo, data]) => (
                    <div key={tipo} className="col-auto">
                      <div className="d-flex align-items-center gap-2">
                        <i className={`bi ${tipo === 'mascota' ? 'bi-heart' : tipo === 'llavero' ? 'bi-key' : 'bi-gem'} text-muted`}></i>
                        <span className="text-capitalize">{tipo}s:</span>
                        <strong>{data.total}</strong>
                        <span className="text-muted small">
                          ({Object.entries(data.planes).map(([p, c]) => `${c} ${p}`).join(', ')})
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sin resultados */}
      {!loading && ventas.length === 0 && !error && (
        <div className="text-center py-5">
          <i className="bi bi-inbox display-1 text-muted"></i>
          <h5 className="mt-3 text-muted">Sin ventas en este período</h5>
          <p className="text-muted">Prueba con otro rango de fechas</p>
        </div>
      )}

      {/* Tabla de ventas */}
      {!loading && ventas.length > 0 && (
        <div className="card">
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table ventas-table mb-0">
                <thead>
                  <tr>
                    <th>Folio</th>
                    <th>Tipo</th>
                    <th>Plan</th>
                    <th>Fecha Liberación</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {ventas.map((venta) => (
                    <tr key={venta.id}>
                      <td>
                        <code className="text-primary">{venta.numeroSerie || venta.folio}</code>
                      </td>
                      <td>
                        <i className={`bi ${venta.tipo === 'mascota' ? 'bi-heart' : venta.tipo === 'llavero' ? 'bi-key' : 'bi-gem'} me-2 text-muted`}></i>
                        <span className="text-capitalize">{venta.tipo}</span>
                      </td>
                      <td>
                        <span className={`badge ${venta.plan === 'premium' ? 'bg-warning text-dark' : venta.plan === 'standard' ? 'bg-info' : 'bg-secondary'}`}>
                          {venta.plan}
                        </span>
                      </td>
                      <td className="text-muted">
                        {formatearFecha(venta.liberadoAt)}
                      </td>
                      <td>
                        {venta.estado === 'activo' ? (
                          <span className="badge bg-success">
                            <i className="bi bi-check-circle me-1"></i>
                            Activado
                          </span>
                        ) : venta.estado === 'inactivo' ? (
                          <span className="badge bg-warning text-dark">
                            <i className="bi bi-clock me-1"></i>
                            Pendiente
                          </span>
                        ) : (
                          <span className="badge bg-secondary">
                            {venta.estado}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VentasTab;
